import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUDGETS = [
  "Under $10k",
  "$10k – $25k",
  "$25k – $50k",
  "$50k – $100k",
  "$100k – $250k",
  "$250k+",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_details: "",
    budget_range: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_details || !form.budget_range) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message received. We'll reply within 1 business day.");
      setForm({ name: "", email: "", company: "", project_details: "", budget_range: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-5">
          <span className="mono-label">[08] Contact</span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.7 }}
            className="mt-4 font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.98]"
          >
            Have a project?
            <br />
            <span className="text-[color:var(--accent)]">Let&apos;s build it.</span>
          </motion.h2>
          <p className="mt-8 text-white/60 text-[15px] leading-relaxed max-w-[42ch]">
            Tell us about the platform you want to ship. We reply personally within one business
            day &mdash; no forms bouncing around a CRM.
          </p>

          <div className="mt-10 space-y-6 border-t border-hairline pt-8">
            <div>
              <div className="mono-label mb-1">Studio</div>
              <div className="text-white text-[15px]">Pune, Maharashtra · India</div>
            </div>
            <div>
              <div className="mono-label mb-1">Web</div>
              <a href="https://beaconitdev.com" className="text-white text-[15px] hover:text-[color:var(--accent)]">beaconitdev.com</a>
            </div>
            <div>
              <div className="mono-label mb-1">Working hours</div>
              <div className="text-white text-[15px]">Mon – Fri · Overlap: US, EU, APAC</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-hairline bg-surface p-6 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <label className="block">
                <span className="mono-label">Name *</span>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[color:var(--accent)] py-3 text-white placeholder:text-white/30 transition-colors"
                  placeholder="Your full name"
                />
              </label>
              <label className="block">
                <span className="mono-label">Email *</span>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[color:var(--accent)] py-3 text-white placeholder:text-white/30 transition-colors"
                  placeholder="you@company.com"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="mono-label">Company</span>
                <input
                  data-testid="contact-company"
                  value={form.company}
                  onChange={onChange("company")}
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[color:var(--accent)] py-3 text-white placeholder:text-white/30 transition-colors"
                  placeholder="Optional"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="mono-label">Project details *</span>
                <textarea
                  data-testid="contact-details"
                  value={form.project_details}
                  onChange={onChange("project_details")}
                  required
                  rows={4}
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[color:var(--accent)] py-3 text-white placeholder:text-white/30 transition-colors resize-none"
                  placeholder="Briefly describe what you'd like to build and the outcome you're after."
                />
              </label>
              <div className="md:col-span-2">
                <span className="mono-label">Budget range *</span>
                <div className="mt-2">
                  <Select
                    value={form.budget_range}
                    onValueChange={(v) => setForm((f) => ({ ...f, budget_range: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-budget"
                      className="w-full bg-transparent border-0 border-b border-white/20 rounded-none focus:border-[color:var(--accent)] focus:ring-0 focus:ring-offset-0 py-6 text-white h-auto justify-between"
                    >
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12151F] border border-hairline text-white">
                      {BUDGETS.map((b) => (
                        <SelectItem
                          key={b}
                          value={b}
                          data-testid={`budget-option-${b}`}
                          className="focus:bg-[color:var(--accent)] focus:text-white cursor-pointer"
                        >
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <p className="mono-label !text-white/50">By submitting, you agree to be contacted about your enquiry.</p>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className={`group inline-flex items-center gap-3 px-6 py-4 text-[14px] font-medium tracking-tight transition-colors ${
                  done
                    ? "bg-white text-[color:var(--bg)]"
                    : "bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)]"
                } disabled:opacity-50`}
              >
                {done ? (
                  <>
                    Message sent <Check size={16} />
                  </>
                ) : (
                  <>
                    {submitting ? "Sending…" : "Send enquiry"} <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
